import { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { getPath, routeNames } from '../../shared/helpers/routes';
import { Description } from '../Asset/shared/Description/Description';
import { useVendor } from '../VendorPovider/VendorPovider';
import { useLazyRender } from '../utils/useLazyRender';
import { Thumbnail } from '../Thumbnail/Thumbnail';
import { AgeLimitLabel } from '../AgeLimitLabel/AgeLimitLabel';
import { getCategoryAgeLimit } from '../../shared/helpers/categoryHelpers';

import { CategoryPromoBadge } from './CategoryPromoBadge';

import type { ImageSources } from '../../helpers/images';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';

import * as styles from './CategoryThumbnail.module.scss';

export interface CategoryThumbnailProps {
  category: SvpCategoryAdditional;
  title?: string;
  description?: string;
  imageSources?: ImageSources;
  onClick?: () => void;
  skipMetadata: boolean;
  onHover?: () => void;
  replacePath?: boolean;
}

export function CategoryThumbnail(props: CategoryThumbnailProps) {
  const {
    category,
    title,
    description,
    imageSources,
    onClick,
    skipMetadata,
    onHover,
    replacePath,
  } = props;

  const { vendor } = useVendor();
  const ref = useRef<HTMLAnchorElement>(null!);
  const { shouldDisplay } = useLazyRender(ref);

  const ageLimit = getCategoryAgeLimit(category);
  const ageLimitTag = useMemo(() => {
    return ageLimit === undefined ? null : (
      <AgeLimitLabel ageLimit={ageLimit} />
    );
  }, [ageLimit]);

  return (
    <Link
      replace={replacePath}
      className={styles.categoryLink}
      onClick={onClick}
      to={getPath(routeNames.category, {
        category,
        vendor,
      })}
      ref={ref}
    >
      <div className={styles.category} onMouseOver={onHover} onFocus={onHover}>
        <Thumbnail
          className={styles.thumbnail}
          imageSources={imageSources}
          variant={ageLimit !== undefined ? 'gradient-bottom' : 'none'}
        >
          <div />
          <div>
            {shouldDisplay && <CategoryPromoBadge category={category} />}
          </div>
          <div>{ageLimitTag}</div>
        </Thumbnail>
        {!skipMetadata && title && <h3 className={styles.title}>{title}</h3>}
        {!skipMetadata && <Description description={description} />}
      </div>
    </Link>
  );
}
